<style scoped>
    #locationMap {
        height : 400px;
    }
</style>

<template>
    <fieldset class="row my-6">

        <legend>Location</legend>

        <article class="alert alert-warning">
            Coordinates inputed have to be in
            <abbr title="World Geodetic System 1984">WGS84</abbr>
            or EPSG:4326 coordinate system, as it is also called.
            Validation will reject coordinates outside of box around Rwanda.
        </article>

        <article class="col-md-6">
            <div id="locationMap"></div>
        </article>

        <article class="col-md-6">

            <section class="form-row my-4">
                <label class="col-md-4 col-form-label">Latitude (<abbr title="World Geodetic System 1984">WGS84</abbr>):</label>

                <div class="col-md-8">
                    <input
                        class="form-control border border-dark"
                        :class="{disabled : (!map.isEditable || isReadOnly)}"
                        type="number"
                        name="latitude"
                        v-model.number="currentLatitude"
                        :min="limitsOfCoordinates.minLatitude"
                        :max="limitsOfCoordinates.maxLatitude"
                        :step="limitsOfCoordinates.step"
                        :disabled=" !map.isEditable || isReadOnly " />

                    <div class="alert alert-danger" v-show="errors.latitude">
                        <span v-for="item of errors.latitude">{{ item }}</span>
                    </div>
                </div>
            </section>

            <section class="form-row my-4">
                <label class="col-md-4 control-label">Longitude (<abbr title="World Geodetic System 1984">WGS84</abbr>):</label>

                <div class="col-md-8">
                    <input
                        class="form-control border border-dark"
                        :class="{disabled : (!map.isEditable || isReadOnly)}"
                        type="number"
                        name="longitude"
                        v-model.number="currentLongitude"
                        :min="limitsOfCoordinates.minLongitude"
                        :max="limitsOfCoordinates.maxLongitude"
                        :step="limitsOfCoordinates.step"
                        :disabled=" !map.isEditable || isReadOnly" />

                    <div class="alert alert-danger" v-show="errors.longitude">
                        <span v-for="item of errors.longitude">{{ item }}</span>
                    </div>
                </div>
            </section>

            <input type="hidden" name="latitude" :value="oldLatitude" />
            <input type="hidden" name="longitude" :value="oldLongitude" />

            <section class="form-group my-4" v-if="!isReadOnly">

                <div class="row" v-if="!map.isEditable">
                    <div class="col-lg-7" v-if="!isReadOnly">
                        <span class="btn btn-block btn-primary border border-dark" @click="enableEditing()">Enable editing</span>
                    </div>

                    <div class="col-lg-5">
                        <span class="btn btn-block btn-primary border border-dark" @click="getAdminUnits()">Check</span>
                    </div>
                </div>

                <div class="row" v-else>
                    <div class="col-sm-6">
                        <span class="btn btn-block btn-secondary border border-dark" @click="restoreOriginalPosition()">Cancel</span>
                    </div>

                    <div class="col-sm-6">
                        <span class="btn btn-block btn-primary border border-dark" @click="updatePosition()">Update position</span>
                    </div>
                </div>

            </section>

            <section class="form-group my-4" v-if="!isReadOnly">

                <div class="row">
                    <label class="col-form-label col-sm-3">Province:</label>
                    <div class="form-control-plaintext col-sm-9">
                        <span class="text-monospace">{{ hierarchy.province[0].name || '' }}</span>
                        <span class="badge badge-secondary">{{ hierarchy.province[0].code || '' }}</span>
                    </div>
                </div>

                <div class="row">
                    <label class="col-form-label col-sm-3">District:</label>
                    <div class="form-control-plaintext col-sm-9">
                        <span class="text-monospace">{{ hierarchy.district[0].name || '' }}</span>
                        <span class="badge badge-secondary">{{ hierarchy.district[0].code || '' }}</span>
                    </div>
                </div>

                <div class="row">
                    <label class="col-form-label col-sm-3">Sector:</label>
                    <div class="form-control-plaintext col-sm-9">
                        <span class="text-monospace">{{ hierarchy.sector[0].name || '' }}</span>
                        <span class="badge badge-secondary">{{ hierarchy.sector[0].code || '' }}</span>
                    </div>
                </div>

                <div class="row">
                    <label class="col-form-label col-sm-3">Cell:</label>
                    <div class="form-control-plaintext col-sm-9">
                        <span class="text-monospace">{{ hierarchy.cell[0].name || '' }}</span>
                        <span class="badge badge-secondary">{{ hierarchy.cell[0].code || '' }}</span>
                    </div>
                </div>

                <div class="row">
                    <label class="col-form-label col-sm-3">Village:</label>
                    <div class="form-control-plaintext col-sm-9">
                        <span class="text-monospace">{{ hierarchy.village[0].name || '' }}</span>
                        <span class="badge badge-secondary">{{ hierarchy.village[0].code || '' }}</span>
                    </div>
                </div>

            </section>

            <section class="form-row my-4">
                <label class="col-form-label col-sm-3">And you end in:</label>
                <div class="form-control-plaintext col-sm-9">
                    <span class="text-monospace">{{ generalEntityName }}</span>
                </div>
            </section>

        </article>
    </fieldset>
</template>

<script>
"use strict";

import Map from "ol/map";
import View from "ol/view";
import Proj from "ol/proj";

import Control from "ol/control";
import FullScreen from "ol/control/fullscreen.js";

import TileLayer from "ol/layer/tile";
import VectorLayer from "ol/layer/vector";

import OsmSource from "ol/source/osm";
import VectorSource from "ol/source/vector";

import Feature from "ol/feature";
import Point from "ol/geom/point";

import WktFormat from "ol/format/wkt";

export default {

    props: {

        limitsOfCoordinates: {
            type: Object,
            required: true
        },

        latitude: {
            type: Number,
            required: true
        },

        longitude: {
            type: Number,
            required: true
        },

        errors: {
            type: Object | Array,
            default: () => { return []; }
        },

        isReadOnly: {
            type: Boolean,
            default: false
        },

        generalEntityName: {
            type: String,
            default: "/"
        }

    },

    data() {
        let roundedLat = Math.round(this.latitude * this.limitsOfCoordinates.round) / this.limitsOfCoordinates.round;
        let roundedLong = Math.round(this.longitude * this.limitsOfCoordinates.round) / this.limitsOfCoordinates.round;

        return {

            map: {
                instance: null,
                isEditable: false
            },

            oldLatitude: roundedLat,
            oldLongitude: roundedLong,

            currentLatitude: roundedLat,
            currentLongitude: roundedLong,

            point: new Point(Proj.fromLonLat([roundedLong, roundedLat])),

            zoom: 8,

            hierarchy : {
                province : [{ name : '', code : 0}],
                district : [{ name : '', code : 0}],
                sector : [{ name : '', code : 0}],
                cell : [{ name : '', code : 0}],
                village : [{ name : '', code : 0}]
            }

        };
    },

    watch: {

        currentLongitude() {
            this.point.setCoordinates(Proj.fromLonLat([this.currentLongitude, this.currentLatitude]));
        },

        currentLatitude() {
            this.point.setCoordinates(Proj.fromLonLat([this.currentLongitude, this.currentLatitude]));
        }

    },

    mounted() {
        let baseLayer = new TileLayer({
            source: new OsmSource()
        });

        let featurePoint = new Feature({
            geometry: this.point
        });

        let locationLayer = new VectorLayer({
            source: new VectorSource({
                features: [featurePoint]
            })
        });

        this.map.instance = new Map({
            target: "locationMap",
            layers: [baseLayer, locationLayer],
            view: new View({
                center: Proj.fromLonLat([this.oldLongitude, this.oldLatitude]),
                zoom: this.zoom
            }),
            controls: Control.defaults().extend([ new FullScreen() ])
        });

    },

    methods: {

        // obtain coordinates from click event and move to click
        mapClick(event) {
            let lonlat = Proj.toLonLat(event.coordinate);

            this.currentLongitude = Math.round(lonlat[0] * this.limitsOfCoordinates.round) / this.limitsOfCoordinates.round;
            this.currentLatitude = Math.round(lonlat[1] * this.limitsOfCoordinates.round) / this.limitsOfCoordinates.round;
        },

        enableEditing() {
            this.map.instance.on("click", this.mapClick);

            this.map.isEditable = true;
        },

        restoreOriginalPosition() {
            this.disableEditing();

            this.currentLatitude = this.oldLatitude;
            this.currentLongitude = this.oldLongitude;
        },

        disableEditing() {
            this.map.instance.un("click", this.mapClick);

            this.map.isEditable = false;
        },

        updatePosition() {
            this.oldLatitude = this.currentLatitude;
            this.oldLongitude = this.currentLongitude;

            this.restoreOriginalPosition();
        },

        getAdminUnits() {
            let locationInWkt =  (new WktFormat()).writeGeometry(this.point, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
            });
            window.axios.get( window.APP_URL +"/location/admin-units?wkt=" + locationInWkt )
                .then((response) => { this.hierarchy = response.data; });
        }

    }

};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/maps/EditLocation.vue